// Automatic savings on this plan: brand deals, product volume pricing, pairings, kit free shipping and free shipping from
// €1,500 are real Shopify discount
// codes created by the Rosvia app (rosvia-app/ops/code-discounts.mjs). This script works out which of those codes the cart qualifies for and
// keeps exactly those on the cart, next to any code the customer typed. It never decides a price: every code carries
// its own conditions in Shopify, which re-checks them on each cart and at checkout, so a stale code simply stops applying.
// Site rules (1 Oct 2026) §01.4: product savings and free shipping never stack. Above the free-shipping value the cart
// uses one of them — the larger, until the customer picks (cart attribute _rosvia_benefit_choice, cart-shipping-meter).
(() => {
  const script = document.currentScript;
  const mapUrl = script?.dataset.map;
  if (!mapUrl) return;
  // Codes this script owns. Anything else on the cart was typed by the customer and is always kept.
  const MANAGED = [/-BRAND-\d+(\.\d+)?%$/i, /-VOL-\d+(\.\d+)?%$/i, /^KIT-[A-Z0-9-]+$/i, /^COMPLIMENTARY-GIFT-[A-Z0-9]+$/i,
    /^PAIRING-[A-Z0-9-]+$/i, /^PAIRSHIP-[A-Z0-9-]+$/i, /^FREESHIP-[A-Z0-9-]+$/i];
  // A complimentary gift line (complimentary-gift.js). It never counts towards any saving or towards the gift minimum.
  const isGift = item => item.properties?._rosvia_gift === 'true';
  const isManaged = code => MANAGED.some(pattern => pattern.test(code));
  // Gift rule (client, 7 Oct 2026): from N units and an order total of at least €X *after* every discount on the cart
  // (product savings and any code the customer typed), converted into the customer's currency. Read from Shopify's own
  // totals, so it is only exact once the codes are on the cart — sync() checks it again after setting them.
  const giftUnitsOk = (cart, gift) => Boolean(gift)
    && cart.items.filter(item => !isGift(item)).reduce((sum, item) => sum + item.quantity, 0) >= gift.min;
  const giftQualifies = (cart, gift) => {
    if (!giftUnitsOk(cart, gift)) return false;
    const giftLines = cart.items.filter(isGift).reduce((sum, item) => sum + item.final_line_price, 0);
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return cart.total_price - giftLines >= (gift.minSubtotal || 0) * rate;
  };
  // Shopify accepts at most 5 product/order codes on one checkout.
  const MAX_PRODUCT_CODES = 5;
  let mapPromise;
  let running = null;
  let queued = false;

  const EMPTY = { brands: {}, kits: {}, pairings: {}, gift: null, freeShipping: null };
  const loadMap = () => (mapPromise ??= fetch(mapUrl).then(r => (r.ok ? r.json() : EMPTY)).catch(() => EMPTY));

  // A percentage off one line, rounded the way Shopify does it: per unit, down to the cent.
  const lineSaving = (item, percent) => Math.floor((item.original_price * percent) / 100) * item.quantity;

  const wanted = (cart, map, slots, typed) => {
    // Kit and pairing lines carry their own benefit only (rules §6 row 9: no cross-contamination), so they never earn
    // another saving.
    const open = cart.items.filter(item => !isGift(item) && !item.properties?._rosvia_kit && !item.properties?._rosvia_pairing);
    // Every code the cart qualifies for, with what it would save on each line it reaches.
    const candidates = [];
    for (const brand of Object.values(map.brands)) {
      const members = new Set(brand.products);
      const lines = open.filter(item => members.has(item.product_id));
      const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
      const tier = [...brand.tiers].reverse().find(t => quantity >= t.min);
      if (tier) candidates.push({ code: tier.code, lines: lines.map(item => [item.key, lineSaving(item, tier.percent)]) });
    }
    const volume = map.volume;
    if (volume?.products) {
      const byProduct = new Map();
      for (const item of open) if (volume.products[item.product_id]) byProduct.set(item.product_id, [...(byProduct.get(item.product_id) || []), item]);
      for (const [productId, lines] of byProduct) {
        // A volume code reaches every line of its product, kit lines included, so it is left off while the product
        // is also in a kit or pairing on this cart.
        if (cart.items.some(item => item.product_id === productId && (item.properties?._rosvia_kit || item.properties?._rosvia_pairing))) continue;
        const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
        const tier = [...volume.tiers].reverse().find(t => quantity >= t.min);
        if (tier) candidates.push({ code: `${volume.products[productId]}-VOL-${tier.percent}%`, lines: lines.map(item => [item.key, lineSaving(item, tier.percent)]) });
      }
    }
    // A group (kit or pairing) counts only for lines added from it, with every member at its minimum.
    const complete = (group, property, id) => {
      const qty = new Map();
      for (const item of cart.items) {
        if (item.properties?.[property] === id) qty.set(item.product_id, (qty.get(item.product_id) || 0) + item.quantity);
      }
      return group.products.length > 0 && group.products.every(pid => (qty.get(pid) || 0) >= group.min);
    };
    // Shopify gives each line only the best product code that reaches it (rules §2: the larger saving wins), so pick
    // codes by what they add on top of the ones already picked, until Shopify's code limit is reached.
    const best = new Map();
    const productCodes = [];
    const gain = candidate => candidate.lines.reduce((sum, [key, saving]) => sum + Math.max(0, saving - (best.get(key) || 0)), 0);
    // The gift code goes first: the customer has already chosen the gift.
    const giftCode = slots > 0 && cart.items.some(isGift) && giftUnitsOk(cart, map.gift) ? map.gift.code : null;
    if (giftCode) productCodes.push(giftCode);
    // A complete pairing's own saving and free shipping come next: they are what the customer added the pairing for.
    const shippingCodes = [];
    // A pairing's own percentage is a product saving too (client, 7 Oct 2026: pairings no longer include free shipping),
    // so it counts when the cart weighs free shipping against product savings.
    let pairingSavings = 0;
    for (const [pairingId, pairing] of Object.entries(map.pairings || {})) {
      if (!complete(pairing, '_rosvia_pairing', pairingId)) continue;
      if (pairing.code && productCodes.length < slots) {
        productCodes.push(pairing.code);
        pairingSavings += cart.items.filter(item => item.properties?._rosvia_pairing === pairingId)
          .reduce((sum, item) => sum + lineSaving(item, pairing.percent || 0), 0);
      }
      if (pairing.shipping) shippingCodes.push(pairing.shipping);
    }
    for (const [kitId, kit] of Object.entries(map.kits)) if (complete(kit, '_rosvia_kit', kitId)) shippingCodes.push(kit.code);
    while (productCodes.length < slots && candidates.length) {
      candidates.sort((a, b) => gain(b) - gain(a));
      if (gain(candidates[0]) <= 0) break;
      const pick = candidates.shift();
      pick.lines.forEach(([key, saving]) => best.set(key, Math.max(saving, best.get(key) || 0)));
      productCodes.push(pick.code);
    }
    // Site rules §01.5 Kit / Pairing: its free shipping stays and the other lines keep their tiered pricing.
    if (shippingCodes.length) return { productCodes, shippingCodes: shippingCodes.slice(0, 1), benefit: 'bundle', savings: 0 };
    // §01.3 / §01.4: from the free-shipping value, free shipping or product savings — never both, and never next to a
    // code the customer typed (store offers do not combine with free shipping).
    const free = map.freeShipping;
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    const orderValue = cart.items.reduce((sum, item) => sum + (isGift(item) ? 0 : item.original_line_price), 0);
    if (!free?.code || typed.length || orderValue < free.min * rate) return { productCodes, shippingCodes: [], benefit: '', savings: 0 };
    const savings = [...best.values()].reduce((sum, saving) => sum + saving, 0) + pairingSavings;
    const choice = cart.attributes?._rosvia_benefit_choice;
    // Until the customer chooses, the larger benefit: product savings against the Secure Delivery fee.
    const useShipping = choice === 'shipping' || choice === 'savings' ? choice === 'shipping' : savings <= free.fee * rate;
    if (!useShipping) return { productCodes, shippingCodes: [], benefit: 'savings', savings };
    // Free shipping: every product saving goes, the gift stays (a standalone benefit, §01.2).
    return { productCodes: giftCode ? [giftCode] : [], shippingCodes: [free.code], benefit: 'shipping', savings };
  };

  // The codes, plus the cart attributes the shipping meter reads (snippets/cart-shipping-meter.liquid), in one request.
  const setCodes = async (codes, attributes) => {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(attributes ? { discount: codes.join(','), attributes } : { discount: codes.join(',') }),
    });
    return response.ok ? response.json() : null;
  };
  const sameCodes = (a, b) => a.length === b.length && a.every(code => b.some(c => c.toUpperCase() === code.toUpperCase()));
  // Remembers the choice made for one cart (lines + the customer's codes), so a re-sync does not flip it back.
  let decision = null;

  const readCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json());

  // At most one gift unit, and none once the order is below the gift minimum (or the gift is switched off): a gift line
  // left in an order that no longer qualifies would be charged at full price.
  const tidyGift = async (cart, map) => {
    const lines = cart.items.filter(isGift);
    if (!lines.length) return cart;
    const keep = giftUnitsOk(cart, map.gift);
    const updates = {};
    let kept = false;
    for (const line of lines) {
      const quantity = keep && !kept && map.gift.variants.includes(line.variant_id) ? 1 : 0;
      if (quantity) kept = true;
      if (line.quantity !== quantity) updates[line.key] = quantity;
    }
    if (!Object.keys(updates).length) return cart;
    await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
    return readCart();
  };

  const sync = async () => {
    const map = await loadMap();
    const before = await readCart();
    const cart = await tidyGift(before, map);
    const giftChanged = cart !== before;
    const current = (cart.discount_codes || []).map(d => d.code);
    const typed = current.filter(code => !isManaged(code));
    const { productCodes, shippingCodes, benefit, savings } = cart.item_count
      ? wanted(cart, map, Math.max(0, MAX_PRODUCT_CODES - typed.length), typed)
      : { productCodes: [], shippingCodes: [], benefit: '', savings: 0 };
    let next = [...typed, ...productCodes, ...shippingCodes];
    const attributes = { _rosvia_benefit: benefit, _rosvia_savings: benefit === 'shipping' ? String(savings) : '' };
    const sameAttributes = Object.entries(attributes).every(([key, value]) => (cart.attributes?.[key] || '') === value);
    const signature = `${cart.items.map(i => `${i.key}:${i.quantity}`).join(',')}|${typed.join(',').toUpperCase()}|${cart.attributes?._rosvia_benefit_choice || ''}`;
    if (decision?.signature === signature) next = decision.codes;
    if (sameCodes(next, current) && sameAttributes) {
      if (giftChanged) document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return;
    }
    let result = await setCodes(next, sameAttributes ? null : attributes);
    if (!result) return;
    // Rules §2: savings never stack on one product and the larger saving wins. A code the customer typed that does not
    // combine with the brand/kit codes shows as not applicable; try it on its own and keep whichever saves more.
    const blocked = typed.length && next.length > typed.length
      && (result.discount_codes || []).some(d => !isManaged(d.code) && !d.applicable);
    if (blocked) {
      const alone = await setCodes(typed);
      if (alone && alone.total_discount > result.total_discount) { result = alone; next = typed; }
      else result = (await setCodes(next)) || result;
    }
    // A gift line must never be charged: if Shopify does not accept the gift code, or the order total after discounts is
    // below the gift value, take the gift out of the cart.
    const giftRefused = map.gift && (result.items || []).some(isGift) && (
      (result.discount_codes || []).some(d => d.code.toUpperCase() === map.gift.code.toUpperCase() && !d.applicable)
      || !giftQualifies(result, map.gift));
    if (giftRefused) {
      const updates = Object.fromEntries((result.items || []).filter(isGift).map(item => [item.key, 0]));
      const removed = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      if (removed.ok) { result = await readCart(); next = next.filter(code => code.toUpperCase() !== map.gift.code.toUpperCase()); result = (await setCodes(next)) || result; }
    }
    decision = { signature, codes: next };
    // Same event the rest of the theme listens to, so the drawer and cart page re-render with Shopify's new totals.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));
  };

  // One sync at a time; a change that arrives mid-sync runs once more afterwards.
  const schedule = () => {
    if (running) { queued = true; return; }
    running = sync().catch(() => {}).finally(() => {
      running = null;
      if (queued) { queued = false; schedule(); }
    });
  };

  // For the gift step: wait until the cart's codes are settled before going to checkout.
  const idle = () => new Promise(resolve => {
    const check = () => (running ? running.then(() => setTimeout(check, 0)) : resolve());
    check();
  });
  window.RosviaDiscounts = {
    sync: () => { schedule(); return idle(); },
    // True once the gift code exists in Shopify (it is in the code map). Until then no gift is offered.
    giftReady: () => loadMap().then(map => Boolean(map.gift)),
    giftQualifies: cart => loadMap().then(map => giftQualifies(cart, map.gift)),
  };

  // The customer's pick between free shipping and product savings (cart-shipping-meter.liquid): stored on the cart, then
  // the codes follow it.
  document.addEventListener('change', async event => {
    const input = event.target.closest('[data-benefit-choice] input[type="radio"]');
    if (!input) return;
    const fieldset = input.closest('[data-benefit-choice]');
    fieldset.setAttribute('aria-busy', 'true');
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _rosvia_benefit_choice: input.value } }),
      });
    } finally {
      schedule();
      await idle();
      fieldset.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:added', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule); else schedule();
})();
